import { ContractError, HttpError, userFacingMessage } from '../../../api';
import { PasscodeError } from '../api/errors';
import type { PasscodeRefusalCode } from '../types/domain.types';

export const TIMED_REFUSALS = ['LOCKED', 'TOO_MANY_SENDS', 'RESEND_TOO_SOON'] as const;
export type TimedRefusalCode = (typeof TIMED_REFUSALS)[number];

interface RefusalFacts {
  readonly message: string;
  readonly attemptsLeft: number | null;
  readonly lockSeconds: number | null;
}

export type Refusal =
  | (RefusalFacts & { readonly code: TimedRefusalCode; readonly retryAfterSeconds: number })
  | (RefusalFacts & { readonly code: Exclude<PasscodeRefusalCode, TimedRefusalCode> | null; readonly retryAfterSeconds: null });

export const isTimedRefusal = (code: PasscodeRefusalCode | null): code is TimedRefusalCode =>
  code !== null && (TIMED_REFUSALS as readonly string[]).includes(code);

export const toRefusal = (error: unknown): Refusal => {
  if (!(error instanceof PasscodeError)) {
    return { code: null, message: userFacingMessage(error) ?? '', attemptsLeft: null, retryAfterSeconds: null, lockSeconds: null };
  }
  const facts: RefusalFacts = { message: error.message, attemptsLeft: error.attemptsLeft, lockSeconds: error.lockSeconds };
  const { refusal: code, retryAfterSeconds } = error;
  if (!isTimedRefusal(code)) return { ...facts, code, retryAfterSeconds: null };
  return retryAfterSeconds === null ? { ...facts, code: null, retryAfterSeconds: null } : { ...facts, code, retryAfterSeconds };
};

export const isTransportFailure = (error: unknown): boolean => {
  if (error instanceof HttpError) return error.status === 0;
  return !(error instanceof ContractError);
};

export type FormProblem =
  | { readonly kind: 'transport' }
  | { readonly kind: 'refused'; readonly refusal: Refusal; readonly retryable: boolean };

export const formProblemOf = (error: unknown): FormProblem => {
  if (isTransportFailure(error)) return { kind: 'transport' };
  const refusal = toRefusal(error);
  return { kind: 'refused', refusal, retryable: refusal.code === null };
};

const IDENTIFIER_REFUSALS: readonly PasscodeRefusalCode[] = [
  'UNKNOWN_IDENTIFIER',
  'CHANNEL_UNAVAILABLE',
  'PHONE_TAKEN',
  'EMAIL_TAKEN',
  'INVALID_EMAIL',
  'INVALID_PHONE',
];
const NEW_ATTEMPT_REFUSALS: readonly PasscodeRefusalCode[] = ['WRONG_CODE', 'CODE_EXPIRED', 'WRONG_PURPOSE'];

const codeIn = (codes: readonly PasscodeRefusalCode[], refusal: Refusal): boolean =>
  refusal.code !== null && codes.includes(refusal.code);

export const isIdentifierRefusal = (refusal: Refusal): boolean => codeIn(IDENTIFIER_REFUSALS, refusal);

export const asksForNewAttempt = (refusal: Refusal): boolean => codeIn(NEW_ATTEMPT_REFUSALS, refusal);
